import React from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Container,
  Grid,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { LISTING_INTRO_QUERY } from "../graphql/pageIntros";

type CanonicalType = "ring" | "necklace" | "earrings" | "bracelet" | string;

type PillarLink = {
  label: string;
  href: string;
  type: string;
};

const ACCENT = "#AF996D";

const ALL_PILLARS: PillarLink[] = [
  { label: "SHOP RINGS", href: "/products-listing?type=ring", type: "ring" },
  {
    label: "SHOP NECKLACES & PENDANTS",
    href: "/products-listing?type=necklace",
    type: "necklace",
  },
  {
    label: "SHOP BRACELETS",
    href: "/products-listing?type=bracelet",
    type: "bracelet",
  },
  {
    label: "SHOP EARRINGS",
    href: "/products-listing?type=earrings",
    type: "earrings",
  },
];

interface ProductIntroProps {
  canonicalType: string;
  metal?: string;
}

export default function ProductIntro({
  canonicalType,
  metal,
}: ProductIntroProps) {
  const { data } = useQuery(LISTING_INTRO_QUERY, {
    variables: {
      productType: canonicalType,
      metal: metal || null,
    },
    fetchPolicy: "cache-and-network",
    skip: !canonicalType,
  });

  const intro = data?.listingIntro;

  // Don't render section at all if no content set
  if (!intro?.content) return null;

  const pillarsForType = ALL_PILLARS.filter((p) => p.type !== canonicalType);

  return (
    <Box sx={{ backgroundColor: "#fff", py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Title */}
        <Typography
          component="h2"
          align="center"
          sx={{
            fontFamily: `"Bodoni Moda", serif`,
            fontSize: { xs: "1.8rem", md: "2.2rem" },
            letterSpacing: ".01em",
            lineHeight: 1.05,
            mb: { xs: 2, md: 2.5 },
          }}
        >
          {intro.metal
            ? `${intro.metal.charAt(0).toUpperCase() + intro.metal.slice(1)} ${canonicalType.charAt(0).toUpperCase() + canonicalType.slice(1)}s`
            : `${canonicalType.charAt(0).toUpperCase() + canonicalType.slice(1)}s`}
        </Typography>

        {/* Rich text content from admin */}
        <Box
          align="center"
          sx={{
            mx: "auto",
            maxWidth: 980,
            color: "text.primary",
            fontSize: { xs: ".8rem", md: "0.9rem" },
            lineHeight: { xs: 1.7, md: 1.82 },
            "& p": { mb: 2 },
            "& h1, & h2, & h3": { fontFamily: `"Bodoni Moda", serif`, mb: 1.5 },
            "& ul, & ol": { pl: 3, mb: 2 },
            "& a": { color: "#1976d2", textDecoration: "underline" },
            "& strong": { fontWeight: 700 },
            "& em": { fontStyle: "italic" },
          }}
          dangerouslySetInnerHTML={{ __html: intro.content }}
        />

        {/* Pillar links */}
        <Grid
          container
          spacing={{ xs: 2.5, md: 3 }}
          sx={{ mt: { xs: 3.5, md: 5 } }}
          justifyContent="center"
        >
          {pillarsForType.map((p) => (
            <Grid
              key={p.label}
              item
              xs={6}
              sm={6}
              md={4}
              lg={2.4 as any}
              xl={2.4 as any}
              sx={{
                "@media (min-width:1200px)": {
                  flexBasis: "20%",
                  maxWidth: "20%",
                },
                display: "grid",
                placeItems: "center",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  "&:hover .barFill": { width: "100%" },
                }}
              >
                <MuiLink
                  href={p.href}
                  underline="none"
                  sx={{
                    display: "inline-block",
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: ".12em",
                    fontWeight: 700,
                    fontSize: { xs: ".68rem", md: ".78rem" },
                    lineHeight: 1.35,
                    transition: "opacity .2s ease",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  {p.label}
                </MuiLink>
                <Box
                  sx={{
                    mt: 1,
                    mx: "auto",
                    width: 120,
                    height: 2,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: ACCENT,
                      opacity: 0.45,
                    }}
                  />
                  <Box
                    className="barFill"
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: 0,
                      transition: "width .35s ease",
                      background: `linear-gradient(to right, ${ACCENT} 0%, ${ACCENT} 60%, rgba(175,153,109,0) 100%)`,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
